import { useReveal } from "../lib/useReveal";
import { ShieldCheck, UserPlus, Calendar, Sparkles, Heart } from "lucide-react";

// 4 steps — exact from original Genesis, restyled to match soberfriend.io aesthetic
const STEPS = [
    {
        n: "01",
        Icon: UserPlus,
        iconBg: "bg-emerald-100",
        iconColor: "text-emerald-600",
        title: "Create a private account",
        body: "Your identity stays protected from the start. No public profile, ever.",
    },
    {
        n: "02",
        Icon: Calendar,
        iconBg: "bg-blue-100",
        iconColor: "text-blue-600",
        title: "Complete daily check-ins",
        body: "Simple reflections to track your progress — mood, cravings, gratitude, whatever surfaces.",
    },
    {
        n: "03",
        Icon: Sparkles,
        iconBg: "bg-purple-100",
        iconColor: "text-purple-600",
        title: "Reflect, journal, or talk to Hope",
        body: "Choose how you want to express yourself — Hope listens, always, without judgment.",
    },
    {
        n: "04",
        Icon: Heart,
        iconBg: "bg-rose-100",
        iconColor: "text-rose-600",
        title: "Get support when you need it",
        body: "We're here whenever you're ready — mentors, meetings, and resources are optional.",
    },
];

export default function HowItWorks() {
    const ref = useReveal();
    return (
        <section
            id="how-it-works"
            ref={ref}
            data-testid="how-it-works-section"
            className="py-20 md:py-24 bg-muted/30 border-y scroll-mt-20"
        >
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-start reveal">
                    <div className="lg:sticky lg:top-24 space-y-6">
                        <div className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-primary/10 text-primary border-0 text-sm font-semibold">
                            <Sparkles className="w-3.5 h-3.5" />
                            Simple Process
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight leading-tight">
                            How Genesis Recovery AI supports you
                        </h2>
                        <p className="text-lg text-muted-foreground leading-relaxed">
                            Recovery is personal. Genesis adapts to the pace that feels right for you
                            — no streak shaming, no pressure, no performance. Start in under two
                            minutes.
                        </p>
                        <ul className="space-y-3 pt-2">
                            {[
                                "No account is ever public — private by default",
                                "No data sold, ever — your story stays yours",
                                "Built-in support resources available when you need extra help",
                                "Export or delete all your data, anytime",
                            ].map((l) => (
                                <li key={l} className="flex items-start gap-3">
                                    <ShieldCheck className="w-6 h-6 text-primary flex-shrink-0" />
                                    <span className="text-foreground font-medium">{l}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="space-y-4">
                        {STEPS.map((s) => (
                            <div
                                key={s.n}
                                data-testid={`how-step-${s.n}`}
                                className="bg-card border rounded-2xl p-6 flex items-start gap-5 hover:shadow-md hover:border-primary/30 transition-all"
                            >
                                <div
                                    className={`w-12 h-12 rounded-xl ${s.iconBg} flex items-center justify-center shrink-0`}
                                >
                                    <s.Icon className={`w-6 h-6 ${s.iconColor}`} />
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-baseline gap-3 mb-1.5">
                                        <span className="text-xs font-bold text-muted-foreground tracking-wider">
                                            STEP {s.n}
                                        </span>
                                    </div>
                                    <h3 className="text-lg font-bold text-foreground mb-1.5">
                                        {s.title}
                                    </h3>
                                    <p className="text-muted-foreground text-sm leading-relaxed">
                                        {s.body}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
